import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, ArrowRight, CheckCircle, TrendingUp, Target, BarChart, Globe, Eye, Users, Zap } from 'lucide-react';

const SEOOptimization: React.FC = () => {
  const features = [
    {
      icon: <Target size={24} />,
      title: 'Keyword Research',
      description: 'In-depth analysis to identify high-value keywords that drive qualified traffic to your website.'
    },
    {
      icon: <BarChart size={24} />,
      title: 'Technical SEO',
      description: 'Optimize your website structure, speed, and technical elements for maximum search engine visibility.'
    },
    {
      icon: <Globe size={24} />,
      title: 'Content Strategy',
      description: 'Create and optimize content that resonates with your audience and ranks well in search results.'
    },
    {
      icon: <Eye size={24} />,
      title: 'Analytics & Reporting',
      description: 'Comprehensive tracking and reporting to measure ROI and continuously improve performance.'
    }
  ];

  const benefits = [
    'Increase organic search traffic by 200%+',
    'Improve search engine rankings for target keywords',
    'Drive qualified leads to your website',
    'Build long-term online authority',
    'Reduce dependency on paid advertising',
    'Track and measure ROI with detailed analytics',
    'Stay ahead of algorithm updates',
    'Local SEO optimization for local businesses'
  ];

  const process = [
    {
      step: '01',
      title: 'SEO Audit & Analysis',
      description: 'Comprehensive analysis of your current SEO performance, identifying opportunities and challenges.'
    },
    {
      step: '02',
      title: 'Strategy Development',
      description: 'Create a custom SEO strategy based on your business goals, target audience, and competitive landscape.'
    },
    {
      step: '03',
      title: 'Implementation',
      description: 'Execute on-page and off-page SEO optimizations, including technical improvements and content creation.'
    },
    {
      step: '04',
      title: 'Monitoring & Optimization',
      description: 'Continuous monitoring, reporting, and optimization to ensure sustained growth and improved rankings.'
    }
  ];

  const pricingTiers = [
    {
      name: 'Local SEO',
      price: '$1,499/mo',
      description: 'Perfect for local businesses and service providers',
      features: [
        'Local keyword research',
        'Google My Business optimization',
        'Local citation building',
        'Review management',
        'Monthly reporting',
        'On-page optimization'
      ]
    },
    {
      name: 'Professional SEO',
      price: '$2,999/mo',
      description: 'Ideal for growing businesses and e-commerce',
      features: [
        'Comprehensive keyword research',
        'Technical SEO audit & fixes',
        'Content strategy & creation',
        'Link building campaigns',
        'Competitor analysis',
        'Advanced analytics setup',
        'Monthly strategy calls'
      ],
      featured: true
    },
    {
      name: 'Enterprise SEO',
      price: 'Custom',
      description: 'For large organizations with complex needs',
      features: [
        'Multi-location SEO',
        'International SEO',
        'Advanced technical SEO',
        'Custom content creation',
        'Dedicated SEO team',
        'Quarterly strategy reviews',
        'Priority support'
      ]
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="page-transition"
    >
      {/* Breadcrumb */}
      <section className="breadcrumb">
        <div className="container">
          <nav className="breadcrumb-nav">
            <Link to="/" className="breadcrumb-link">Home</Link>
            <span className="breadcrumb-separator">→</span>
            <Link to="/services" className="breadcrumb-link">Services</Link>
            <span className="breadcrumb-separator">→</span>
            <span className="breadcrumb-current">SEO Optimization</span>
          </nav>
        </div>
      </section>

      {/* Hero Section */}
      <section className="service-hero">
        <div className="container">
          <div className="hero-content">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="hero-icon"
            >
              <Search size={60} />
            </motion.div>
            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              SEO Optimization Services
            </motion.h1>
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="hero-description"
            >
              Dominate search engine rankings with our data-driven SEO strategies. We help businesses increase organic traffic, 
              improve visibility, and drive qualified leads through proven search engine optimization techniques.
            </motion.p>
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="hero-actions"
            >
              <Link to="/contact" className="btn btn-primary">
                Start SEO Campaign <ArrowRight size={20} />
              </Link>
              <Link to="/portfolio" className="btn btn-secondary">
                View Results
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-8">
        <div className="container">
          <div className="text-center mb-4">
            <h2>Our SEO Approach</h2>
            <p className="section-description">
              We use advanced SEO techniques and AI-powered tools to deliver measurable results for your business.
            </p>
          </div>
          <div className="grid grid-2">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 * index }}
                className="card feature-card"
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <div className="benefits-content">
            <div className="benefits-text">
              <h2>Drive More Qualified Traffic with Professional SEO</h2>
              <p>
                Search engine optimization is one of the most effective ways to attract qualified leads to your website. 
                Our comprehensive SEO services are designed to improve your search rankings, increase organic traffic, 
                and ultimately drive more revenue for your business.
              </p>
              <p>
                We stay up-to-date with the latest Google algorithm changes and search engine best practices to ensure 
                your website maintains strong rankings over time. Our data-driven approach means every strategy is 
                backed by thorough research and continuous performance monitoring.
              </p>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.1 * index }}
                  >
                    <CheckCircle size={20} />
                    {benefit}
                  </motion.li>
                ))}
              </ul>
            </div>
            <div className="benefits-visual">
              <div className="benefit-stat">
                <TrendingUp size={32} />
                <h3>250%</h3>
                <p>Average traffic increase</p>
              </div>
              <div className="benefit-stat">
                <Users size={32} />
                <h3>85%</h3>
                <p>Lead quality improvement</p>
              </div>
              <div className="benefit-stat">
                <Zap size={32} />
                <h3>90 days</h3>
                <p>To see initial results</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-8">
        <div className="container">
          <div className="text-center mb-4">
            <h2>Our Proven SEO Process</h2>
            <p className="section-description">
              A systematic approach that has helped hundreds of businesses achieve top search engine rankings.
            </p>
          </div>
          <div className="process-grid">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 * index }}
                className="process-step"
              >
                <div className="process-number">{step.step}</div>
                <div className="process-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <div className="container">
          <div className="text-center mb-4">
            <h2>SEO Service Packages</h2>
            <p className="section-description">
              Choose the SEO package that aligns with your business goals and budget. All plans include ongoing optimization.
            </p>
          </div>
          <div className="grid grid-3">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 * index }}
                className={`card pricing-card ${tier.featured ? 'featured' : ''}`}
              >
                {tier.featured && <div className="featured-badge">Most Popular</div>}
                <h3>{tier.name}</h3>
                <div className="pricing-price">{tier.price}</div>
                <p className="pricing-description">{tier.description}</p>
                <ul className="pricing-features">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex}>
                      <CheckCircle size={16} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="btn btn-primary pricing-btn">
                  Choose {tier.name}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Dominate Search Rankings?</h2>
            <p>
              Let's analyze your website and create a custom SEO strategy that drives real results. 
              Get started with a free SEO audit and consultation.
            </p>
            <div className="cta-actions">
              <Link to="/contact" className="btn btn-primary">
                Get Free SEO Audit <ArrowRight size={20} />
              </Link>
              <Link to="/services" className="btn btn-secondary">
                View All Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        .breadcrumb {
          padding: 2rem 0 1rem;
          background: rgba(0, 0, 0, 0.2);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .breadcrumb-nav {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
        }

        .breadcrumb-link {
          color: #94a3b8;
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .breadcrumb-link:hover {
          color: #00D4FF;
        }

        .breadcrumb-separator {
          color: #64748b;
        }

        .breadcrumb-current {
          color: #00D4FF;
          font-weight: 500;
        }

        .service-hero {
          padding: 6rem 0 4rem;
          text-align: center;
          background: linear-gradient(135deg, rgba(0, 212, 255, 0.05), rgba(138, 43, 226, 0.05));
        }

        .hero-content {
          max-width: 800px;
          margin: 0 auto;
        }

        .hero-icon {
          color: #00D4FF;
          margin-bottom: 2rem;
          display: flex;
          justify-content: center;
        }

        .hero-description {
          font-size: 1.3rem;
          color: #94a3b8;
          line-height: 1.6;
          margin-bottom: 3rem;
        }

        .hero-actions {
          display: flex;
          gap: 1.5rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .section-description {
          font-size: 1.2rem;
          color: #94a3b8;
          max-width: 600px;
          margin: 0 auto;
        }

        .feature-card {
          text-align: center;
          transition: all 0.3s ease;
        }

        .feature-card:hover {
          transform: translateY(-5px);
        }

        .feature-icon {
          color: #00D4FF;
          margin-bottom: 1.5rem;
          display: flex;
          justify-content: center;
        }

        .benefits-section {
          padding: 6rem 0;
          background: rgba(0, 0, 0, 0.2);
        }

        .benefits-content {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 4rem;
          align-items: start;
        }

        .benefits-text h2 {
          margin-bottom: 2rem;
        }

        .benefits-text p {
          color: #94a3b8;
          margin-bottom: 1.5rem;
          font-size: 1.1rem;
          line-height: 1.7;
        }

        .benefits-list {
          list-style: none;
          margin-top: 2rem;
        }

        .benefits-list li {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
          color: #cbd5e1;
          font-size: 1.1rem;
        }

        .benefits-list svg {
          color: #00FF7F;
          flex-shrink: 0;
        }

        .benefits-visual {
          display: grid;
          gap: 2rem;
        }

        .benefit-stat {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(0, 212, 255, 0.2);
          border-radius: 15px;
          padding: 2rem;
          text-align: center;
          transition: all 0.3s ease;
        }

        .benefit-stat:hover {
          background: rgba(0, 212, 255, 0.05);
          box-shadow: 0 8px 25px rgba(0, 212, 255, 0.1);
        }

        .benefit-stat svg {
          color: #00D4FF;
          margin-bottom: 1rem;
        }

        .benefit-stat h3 {
          font-size: 2rem;
          color: #00FF7F;
          margin-bottom: 0.5rem;
        }

        .benefit-stat p {
          color: #94a3b8;
          margin: 0;
          font-size: 0.9rem;
        }

        .process-grid {
          display: grid;
          gap: 2rem;
          max-width: 800px;
          margin: 0 auto;
        }

        .process-step {
          display: flex;
          gap: 2rem;
          align-items: start;
          padding: 2rem;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 15px;
          transition: all 0.3s ease;
        }

        .process-step:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(0, 212, 255, 0.3);
        }

        .process-number {
          font-size: 2rem;
          font-weight: 700;
          color: #00D4FF;
          flex-shrink: 0;
          width: 60px;
          text-align: center;
        }

        .process-content h3 {
          color: #8A2BE2;
          margin-bottom: 1rem;
        }

        .process-content p {
          color: #94a3b8;
          margin: 0;
          line-height: 1.6;
        }

        .pricing-section {
          padding: 6rem 0;
          background: linear-gradient(135deg, rgba(0, 212, 255, 0.02), rgba(138, 43, 226, 0.02));
        }

        .pricing-card {
          text-align: center;
          position: relative;
          transition: all 0.3s ease;
        }

        .pricing-card.featured {
          border-color: #00D4FF;
          box-shadow: 0 8px 32px rgba(0, 212, 255, 0.15);
          transform: scale(1.05);
        }

        .featured-badge {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, #00D4FF, #8A2BE2);
          color: white;
          padding: 0.5rem 1.5rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .pricing-price {
          font-size: 2.5rem;
          font-weight: 700;
          color: #00D4FF;
          margin: 1rem 0;
        }

        .pricing-description {
          color: #94a3b8;
          margin-bottom: 2rem;
        }

        .pricing-features {
          list-style: none;
          text-align: left;
          margin-bottom: 2rem;
        }

        .pricing-features li {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
          color: #cbd5e1;
        }

        .pricing-features svg {
          color: #00FF7F;
          flex-shrink: 0;
        }

        .pricing-btn {
          width: 100%;
          justify-content: center;
        }

        .service-cta {
          padding: 6rem 0;
          text-align: center;
          background: linear-gradient(135deg, rgba(0, 212, 255, 0.05), rgba(138, 43, 226, 0.05));
        }

        .cta-content {
          max-width: 600px;
          margin: 0 auto;
        }

        .cta-content h2 {
          margin-bottom: 1.5rem;
        }

        .cta-content p {
          font-size: 1.2rem;
          color: #94a3b8;
          margin-bottom: 2.5rem;
        }

        .cta-actions {
          display: flex;
          gap: 1.5rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        @media (max-width: 1024px) {
          .benefits-content {
            grid-template-columns: 1fr;
            gap: 3rem;
          }
        }

        @media (max-width: 768px) {
          .service-hero {
            padding: 4rem 0 3rem;
          }

          .hero-actions {
            flex-direction: column;
            align-items: center;
          }

          .process-step {
            flex-direction: column;
            text-align: center;
            gap: 1rem;
          }

          .process-number {
            width: auto;
          }

          .pricing-card.featured {
            transform: none;
          }

          .cta-actions {
            flex-direction: column;
            align-items: center;
          }

          .btn {
            width: 100%;
            max-width: 280px;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default SEOOptimization;